import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion'
import {
  clearSongList,
  fetchHotBoxSongs,
} from 'spa/action_creators/hot_box_songs'
import { messages as comparisonMessages } from '../comparison/localizedMessages'
import {
  ComparisonTableSection,
  PricingSection,
} from '../comparison/comparison'
import {
  CURRENCY_SYMBOL_MAP,
  PROMOS,
  promoStringResource,
} from 'spa/shared/utils'
import { DynamicMessage } from '../../components/message'
import {
  faqMessages,
  featuresMessages,
  localizedMessages,
  promoMessages,
  testimonialsMessages,
} from './localizedMessages'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedMessage } from 'react-intl'
import { Heading } from 'spa/components/typography'
import { Helmet } from 'react-helmet'
import { hotBoxSongsSelector } from 'spa/selectors/songs'
import { injectIntl } from 'react-intl'
import { IntegrationsLogos } from '../integrations/integrations'
import { messages as integrationsMessages } from '../integrations/localizedMessages'
import {
  isMobileSelector,
  isTabletSelector,
} from 'spa/components/../selectors/device'
import { Knotch } from 'spa/components/knotch'
import { Link } from 'react-router-dom'
import { Loading } from 'spa/components/loading'
import { localeSelector } from 'spa/selectors/users'
import { LOCALIZED_MESSAGES } from 'spa/components/table/messages'
import { loggedInSelector } from 'spa/selectors/users'
import { openModal, SIGNUP_MODAL } from 'spa/action_creators/modals'
import { Redirect } from 'react-router-dom'
import {
  requestPromosPricing,
  setActivePromo,
} from 'spa/action_creators/promos'
import { seoMessages } from 'spa/components/app_wrapper/localizedMessages'
import { SongTable } from 'spa/components/table'
import { TextStyle } from 'spa/components/typography'
import { TRACKLIST } from 'spa/components/../shared/constants'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { useQueryParameters } from 'spa/hooks/use_query_parameters'
import classNames from 'classnames'
import debounce from 'lodash/debounce'
import djcIcon from 'spa/images/svg/djc_logo_icon.svg'
import PromoSignUp from 'spa/components/promosignup'
import PropTypes from 'prop-types'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import styles from './styles'
import SubscribeButton from 'spa/components/subscribe_button'

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is DJcity?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'DJcity is a leading download record pool for DJs. Founded in 2000, it’s one of the longest-running and most trusted sources of high-quality music downloads to beginner and professional DJs worldwide.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does DJcity work with Serato and other DJ software?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Yes! All music downloaded from DJcity is compatible with Serato, Rekordbox, Virtual DJ, Traktor, and other major DJ software. 
          
          <a href="/integrations">Learn more about DJ software integration here.</a>`,
      },
    },
    {
      '@type': 'Question',
      name: 'How much does DJcity cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `New users can get started for just $10!

          Afterward, 6-months access is $25/mo ($150 billed bi-annually), and 1-month access is $30/mo (billed monthly). All plans auto-renew, and you can cancel anytime.
          
          <a href="/#pricing">Check out our pricing section for more details.</a>`,
      },
    },
    {
      '@type': 'Question',
      name: 'Why is DJcity better than other record pools?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `All DJcity plans provide full access to a massive global music catalog with 250+ genres, exclusives edits and remixes, and expert curated playlists.
          
          <a href="/comparison">Find out why DJs choose DJcity over other record pools.</a>`,
      },
    },
  ],
}

function Marketing({ intl }) {
  const { offerName } = useParams()
  const location = useLocation()
  const dispatch = useDispatch()
  const [{ partner }] = useQueryParameters()

  const isLoggedIn = useSelector(loggedInSelector)
  const isMobile = useSelector(isMobileSelector)
  const isTablet = useSelector(isTabletSelector)
  const isLoadingResources = useSelector(
    state => state.stringResources.isFetching
  )
  const { isFetching, promoData, promoError } = useSelector(
    state => state.promos
  )
  const activeSubscription = useSelector(
    state => state.currentUser?.user?.activeSubscription
  )
  const songs = useSelector(hotBoxSongsSelector)
  const locale = useSelector(localeSelector)

  const [isOpen, setOpen] = useState(null)
  const [isMenuVisible, setMenuVisible] = useState(false)
  const [selectedMenu, setSelectedMenu] = useState()

  const handleFaq = ([uuid]) =>
    uuid === isOpen ? setOpen(null) : setOpen(uuid)

  const optImg = url => {
    const urlBase = getPromoStringResource(url).split('.png')[0]
    return urlBase + (!isMobile && !isTablet ? '@2x' : '') + '.png'
  }

  const getPromoStringResource = (resource, values) => {
    return promoStringResource(resource, values, offerName)
  }

  const scrollToSection = id => {
    history.pushState(undefined, '', '#' + id)
    const section = document.getElementById(id)
    setSelectedMenu(id)

    if (section) {
      window.scrollTo({
        behavior: 'smooth',
        top:
          section.getBoundingClientRect().top +
          window.pageYOffset -
          (isMobile ? 130 : 0),
      })
      if (id === 'signup') {
        setTimeout(() => section.querySelector('input').focus(), 1000)
      }
    }
  }

  useLayoutEffect(() => {
    if (location.hash) {
      setTimeout(() => scrollToSection(location.hash.substring(1)), 1000)
    }
  }, [isLoadingResources, isFetching])

  useEffect(() => {
    const onScroll = () => {
      const sections = document.querySelectorAll('.menuSection')
      let current = ''

      sections.forEach(section => {
        const sectionTop = section.offsetTop - (isMobile ? 130 : 0)
        const sectionHeight = section.clientHeight
        if (pageYOffset >= sectionTop - sectionHeight / 3) {
          current = section.getAttribute('id')
        }
      })
      setSelectedMenu(current)
    }

    const debouncedOnScroll = debounce(onScroll, 50)
    window.addEventListener('scroll', debouncedOnScroll)
    return () => {
      window.removeEventListener('scroll', debouncedOnScroll)
    }
  }, [])

  useEffect(() => {
    dispatch(
      fetchHotBoxSongs({
        pagination: {
          page: 1,
          pageSize: 5,
        },
      })
    )

    return () => dispatch(clearSongList())
  }, [locale, isLoggedIn, dispatch])

  useEffect(() => {
    if (partner || offerName) {
      dispatch(setActivePromo(partner || offerName))
    }
  }, [partner, offerName])

  useEffect(() => {
    if (!promoData) {
      dispatch(
        requestPromosPricing({
          promoName: partner || offerName || PROMOS.NETNEW,
        })
      )
    }
  }, [promoData])

  if (promoError) return <Redirect to="/notfound" />

  if (isFetching || isLoadingResources || !promoData)
    return <Loading intl={intl} withHelp={true} />

  return (
    <div
      className={classNames('darkMode', styles.page)}
      style={{
        backgroundImage: `url(${getPromoStringResource(
          localizedMessages.marketing_homepage_background
        )})`,
      }}
    >
      <Helmet
        defer={false}
        htmlAttributes={{
          itemScope: undefined,
          itemType: 'https://schema.org/FAQPage',
        }}
      >
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      {/*MENU*/}
      <div className={styles.menuContainer} id="floatingMenu">
        <div
          className={classNames(styles.menu, {
            [styles.mobileGapVisible]: isMenuVisible,
          })}
        >
          {isMobile && (
            <div
              className={styles.menuToggle}
              onClick={() => setMenuVisible(!isMenuVisible)}
            >
              <FontAwesomeIcon icon={isMenuVisible ? 'times' : 'bars'} />
            </div>
          )}
          <div
            className={classNames('pricing', styles.menuButton, {
              [styles.mobileMenuVisible]: isMenuVisible,
              [styles.menuSelected]: selectedMenu === 'pricing',
            })}
            onClick={() => scrollToSection('pricing')}
          >
            {intl.formatMessage(localizedMessages.menu_1)}
          </div>
          <div
            className={classNames('comparison', styles.menuButton, {
              [styles.mobileMenuVisible]: isMenuVisible,
              [styles.menuSelected]: selectedMenu === 'comparison',
            })}
            onClick={() => scrollToSection('comparison')}
          >
            {intl.formatMessage(localizedMessages.menu_2)}
          </div>
          <div
            className={classNames('integrations', styles.menuButton, {
              [styles.mobileMenuVisible]: isMenuVisible,
              [styles.menuSelected]: selectedMenu === 'integrations',
            })}
            onClick={() => scrollToSection('integrations')}
          >
            {intl.formatMessage(localizedMessages.menu_3)}
          </div>
          <div
            className={classNames('testimonials', styles.menuButton, {
              [styles.mobileMenuVisible]: isMenuVisible,
              [styles.menuSelected]: selectedMenu === 'testimonials',
            })}
            onClick={() => scrollToSection('testimonials')}
          >
            {intl.formatMessage(localizedMessages.menu_4)}
          </div>
          <div
            className={classNames('faq', styles.menuButton, {
              [styles.mobileMenuVisible]: isMenuVisible,
              [styles.menuSelected]: selectedMenu === 'faq',
            })}
            onClick={() => scrollToSection('faq')}
          >
            {intl.formatMessage(localizedMessages.menu_5)}
          </div>

          {!activeSubscription && (
            <div
              className={classNames(styles.signupButton, {
                [styles.mobileMenuVisible]: isMenuVisible || activeSubscription,
              })}
              onClick={() => dispatch(openModal(SIGNUP_MODAL))}
            >
              {intl.formatMessage(localizedMessages.menu_6)}
            </div>
          )}
        </div>
      </div>

      {/*HEADER*/}
      <section
        className={classNames(styles.promo, {
          [styles.loggedIn]: isLoggedIn,
        })}
        id="signup"
      >
        <img src={optImg(promoMessages.background)} />
        <div className={styles.promoContent}>
          <div className={styles.promoHeader}>
            <div className={styles.promoHeaderRow1}>
              <img src={djcIcon} />
              <TextStyle
                className={styles.promoHeaderSubTitle}
                color="white"
                variant="label"
              >
                {getPromoStringResource(promoMessages.title)}
              </TextStyle>
            </div>
            <div className={styles.promoHeaderRow2}>
              <TextStyle
                className={styles.promoHeaderTitle}
                color="white"
                variant="label"
              >
                {getPromoStringResource(promoMessages.price, {
                  currency: CURRENCY_SYMBOL_MAP[promoData.promoCurrency] || '$',
                  price: promoData.promoPrice || 0,
                })}
              </TextStyle>
            </div>
            <div className={styles.promoHeaderRow3}>
              <TextStyle
                className={styles.promoHeaderTitle}
                color="white"
                variant="label"
              >
                {getPromoStringResource(promoMessages.subtitle, {
                  currency: CURRENCY_SYMBOL_MAP[promoData.promoCurrency] || '$',
                  price: promoData.promoPrice || 0,
                })}
              </TextStyle>
            </div>
          </div>
          {!isLoggedIn && (
            <div className={classNames('menuSection', styles.promoSignup)}>
              <PromoSignUp
                currency={CURRENCY_SYMBOL_MAP[promoData.promoCurrency] || '$'}
                offerName={offerName}
                price={promoData.promoPrice || 0}
              />
            </div>
          )}
        </div>
      </section>

      {/*SUBSCRIPTION SECTION*/}
      <section className={styles.subscription}>
        <div className={styles.subscriptionContent}>
          <Link
            className={styles.url}
            to={getPromoStringResource(featuresMessages[0].url)}
          >
            <div className={styles.subscriptionItem}>
              <img src={optImg(featuresMessages[0].icon)} />
              <div>
                <div className={styles.subscriptionItemTitle}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[0].title)}
                  </TextStyle>
                </div>
                <div className={styles.subscriptionItemSubTitle}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[0].subTitle)}
                  </TextStyle>
                </div>
              </div>
            </div>
          </Link>
          <Link
            className={styles.url}
            to={getPromoStringResource(featuresMessages[1].url)}
          >
            <div className={styles.subscriptionItem}>
              <img src={optImg(featuresMessages[1].icon)} />
              <div>
                <div className={styles.subscriptionItemTitle}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[1].title)}
                  </TextStyle>
                </div>
                <div className={styles.subscriptionItemSubTitle}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[1].subTitle)}
                  </TextStyle>
                </div>
              </div>
            </div>
          </Link>
          <Link
            className={styles.url}
            to={getPromoStringResource(featuresMessages[2].url)}
          >
            <div className={styles.subscriptionItem}>
              <img src={optImg(featuresMessages[2].icon)} />
              <div>
                <div className={styles.subscriptionItemTitle}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[2].title)}
                  </TextStyle>
                </div>
                <div className={styles.subscriptionItemSubTitle}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[2].subTitle)}
                  </TextStyle>
                </div>
              </div>
            </div>
          </Link>

          <div className={styles.sidebarRow}>
            <TextStyle uppercase variant="extra-bold">
              <Knotch
                align="left"
                className={styles.sidebarKnotch}
                displayType="inlineBlock"
                size="small"
              />
              <Link className={styles.listTitle} to="/most-popular">
                <FormattedMessage
                  defaultMessage="Most Popular"
                  id="djcity.common.most_popular"
                />
              </Link>
            </TextStyle>
            {isFetching ? (
              <Loading />
            ) : (
              <SongTable
                firstColumnHeaderLabel={intl.formatMessage(
                  LOCALIZED_MESSAGES.records
                )}
                showNumbers
                songs={songs}
                trackList={TRACKLIST.HOTBOX}
              />
            )}
          </div>
          <Link
            className={styles.url}
            to={getPromoStringResource(featuresMessages[5].url)}
          >
            <div className={styles.subscriptionItemMiddle}>
              <img src={optImg(featuresMessages[5].icon)} />
              <div>
                <button>
                  {intl.formatMessage({
                    defaultMessage: 'Browse catalog',
                    id: 'djcity.common.sidebar.browse',
                  })}
                </button>
              </div>
            </div>
          </Link>

          <Link
            className={styles.url}
            to={getPromoStringResource(featuresMessages[3].url)}
          >
            <div className={styles.subscriptionItem}>
              <img src={optImg(featuresMessages[3].icon)} />
              <div>
                <div className={styles.subscriptionItemTitle}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[3].title)}
                  </TextStyle>
                </div>
                <div className={styles.subscriptionItemSubTitle}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[3].subTitle)}
                  </TextStyle>
                </div>
              </div>
            </div>
          </Link>
          <Link
            className={styles.url}
            to={getPromoStringResource(featuresMessages[4].url)}
          >
            <div className={styles.subscriptionItemLast}>
              <div>
                <img src={optImg(featuresMessages[4].icon)} />
              </div>
              <div>
                <div className={styles.subscriptionItemTitleLast}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[4].title)}
                  </TextStyle>
                </div>
                <div className={styles.subscriptionItemSubTitleLast}>
                  <TextStyle color="white">
                    {getPromoStringResource(featuresMessages[4].subTitle)}
                  </TextStyle>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/*PRICING SECTION*/}
      <div className="menuSection" id="pricing">
        <PricingSection />
      </div>

      {/*COMPARISON TABLE*/}
      <div
        className={classNames('menuSection', styles.comparison)}
        id="comparison"
      >
        <div className={styles.comparisonHeader}>
          <Link to="/comparison">
            <h1 aria-level={1}>
              {intl.formatMessage(comparisonMessages.title)}
            </h1>
          </Link>
          <TextStyle color="white">
            {intl.formatMessage(comparisonMessages.subtitle)}
          </TextStyle>
        </div>
      </div>
      <ComparisonTableSection className={styles.comparison} />

      {/*INTEGRATIONS*/}
      <div
        className={classNames('menuSection', styles.integrations)}
        id="integrations"
      >
        <div className={styles.integrationsHeader}>
          <Link to="/integrations">
            <h1 aria-level={1}>
              {intl.formatMessage(integrationsMessages.title)}
            </h1>
          </Link>
          <TextStyle color="white">
            {intl.formatMessage(integrationsMessages.subtitle)}
          </TextStyle>
        </div>
      </div>
      <section className={styles.integrations}>
        <IntegrationsLogos />
      </section>

      {/*RECORD POOLS*/}
      <section className={styles.pools}>
        <div className={styles.poolsWrapper}>
          <img
            className={styles.poolsImage}
            src={getPromoStringResource(localizedMessages.marketing_poolsImage)}
          />
          <Link className={styles.url} to="/global">
            <TextStyle
              className={styles.poolsTitle}
              color="white"
              dangerouslySetInnerHTML={{
                __html: getPromoStringResource(
                  localizedMessages.marketing_poolsTitle
                ),
              }}
            ></TextStyle>
          </Link>
        </div>
        <TextStyle
          className={styles.poolsSubTitle}
          color="white"
          dangerouslySetInnerHTML={{
            __html: getPromoStringResource(
              localizedMessages.marketing_poolsContent
            ),
          }}
        />
      </section>

      {/*EXCLUSIVE SECTION*/}
      <section className={styles.exclusives}>
        <div className={styles.exclusivesWrapper}>
          <Link className={styles.url} to="/genres">
            <TextStyle
              className={styles.exclusivesTitle}
              color="white"
              dangerouslySetInnerHTML={{
                __html: getPromoStringResource(
                  localizedMessages.exclusives_title
                ),
              }}
            ></TextStyle>
          </Link>
          <TextStyle className={styles.exclusivesContent} color="white">
            {getPromoStringResource(localizedMessages.exclusives_content)}
          </TextStyle>
        </div>

        <div className={styles.exclusivesImages}>
          <Link className={styles.url} to="/genres">
            <img
              alt={getPromoStringResource(
                localizedMessages.exclusives_remixersAlt
              )}
              className={styles.exclusivesImagesRow1}
              src={optImg(localizedMessages.exclusives_remixers)}
            />
          </Link>
          <div className={styles.exclusivesImagesRow2}>
            <Link className={styles.url} to="/genres">
              <img src={optImg(localizedMessages.exclusives_remixer)} />
            </Link>
            <Link className={styles.url} to="/genres">
              <TextStyle
                className={styles.exclusivesTracks}
                color="white"
                dangerouslySetInnerHTML={{
                  __html: getPromoStringResource(
                    localizedMessages.exclusives_tracks
                  ),
                }}
              />
            </Link>
          </div>
        </div>
      </section>

      {/*TESTIMONIALS SECTION*/}
      <section
        className={classNames('menuSection', styles.testimonials)}
        id="testimonials"
      >
        <article>
          <div>
            <ul>
              {testimonialsMessages.map((testimonial, index) => (
                <li key={'testimonial' + index}>
                  <img
                    alt={intl.formatMessage(testimonial.text)}
                    src={intl.formatMessage(testimonial.url)}
                  />
                  <div>{intl.formatMessage(testimonial.text)}</div>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <ul>
              {testimonialsMessages.map((testimonial, index) => (
                <li key={'testimonial' + index}>
                  <img
                    alt={intl.formatMessage(testimonial.text)}
                    src={intl.formatMessage(testimonial.url)}
                  />
                  <div>{intl.formatMessage(testimonial.text)}</div>
                </li>
              ))}
            </ul>
          </div>
        </article>
      </section>

      {/* FAQ SECTION */}
      <section className={classNames('menuSection', styles.faq)} id="faq">
        <div className={styles.faqWrapper}>
          <TextStyle className={styles.faqTitle} color="white">
            {intl.formatMessage(localizedMessages.faq_title)}
          </TextStyle>
          <TextStyle className={styles.faqContent} color="white">
            {intl.formatMessage(localizedMessages.faq_subtitle)}
          </TextStyle>
        </div>

        <div>
          <Accordion
            // allowMultipleExpanded
            className={styles.accordion}
            onChange={handleFaq}
          >
            {faqMessages.map((faq, idx) => (
              <AccordionItem
                className={styles.accordionItem}
                dangerouslySetExpanded={isOpen === faq.uuid}
                itemprop="mainEntity"
                itemscope
                itemtype="https://schema.org/Question"
                key={'faq_' + idx}
                uuid={faq.uuid}
              >
                <AccordionItemHeading className={styles.accordionHeading}>
                  <AccordionItemButton
                    className={styles.accordionButton}
                    itemprop="name"
                  >
                    <DynamicMessage
                      aria-level={3}
                      defaultMessage={faq.msg.question.defaultMessage}
                      id={faq.msg.question.id}
                      tag="h3"
                    />
                    <FontAwesomeIcon
                      color={isOpen === faq.uuid ? 'red' : 'white'}
                      icon={`chevron-${isOpen === faq.uuid ? 'down' : 'right'}`}
                    />
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel
                  className={styles.accordionPanel}
                  itemprop="acceptedAnswer"
                  itemscope
                  itemtype="https://schema.org/Answer"
                >
                  <DynamicMessage
                    defaultMessage={faq.msg.answer.defaultMessage}
                    id={faq.msg.answer.id}
                    itemprop="text"
                  />
                </AccordionItemPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/*PRICE*/}
      {!activeSubscription && (
        <section
          className={styles.pricing}
          style={{
            backgroundImage: `url(${optImg(
              localizedMessages.marketing_crowd_banner
            )})`,
          }}
        >
          <div>
            <TextStyle className={styles.pricingTitle} color="white">
              {offerName
                ? getPromoStringResource(promoMessages.price_footer, {
                    currency:
                      CURRENCY_SYMBOL_MAP[promoData.promoCurrency] || '$',
                    price: promoData.promoPrice || 0,
                  })
                : getPromoStringResource(
                    localizedMessages.pricing_getStartedToday
                  )}
            </TextStyle>
            <Heading headingVariant="disguisedH1">
              {intl.formatMessage(seoMessages.marketingH1)}
            </Heading>
            <SubscribeButton
              className={styles.subscribeButton}
              onClick={!activeSubscription && (() => scrollToSection('signup'))}
            >
              {getPromoStringResource(localizedMessages.subscribeNow)}
            </SubscribeButton>
          </div>
        </section>
      )}
    </div>
  )
}

Marketing.propTypes = {
  intl: PropTypes.object,
}

export default injectIntl(Marketing)
